import React, { type Ref, useCallback, useEffect, useRef } from 'react'
import { clsx } from 'clsx'
import type { ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form'
import { components as selectComponents, type MenuProps, type SelectInstance } from 'react-select'
import AsyncCreatableSelect from 'react-select/async-creatable'
import ReactAsyncSelect from 'react-select/async'

import { useFormField } from '@hooks/useFormField'
import { scopedTranslation } from '@utils/I18n'
import type { SelectOption } from '@utils/types'

const tShared = scopedTranslation('shared')

const SEARCH_DEBOUNCE_MS = 300

type LoadOptions = (inputValue: string) => Promise<SelectOption[]>

function useDebouncedLoadOptions(loadOptions: LoadOptions | undefined): LoadOptions | undefined {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const settleSuperseded = useRef<((options: SelectOption[]) => void) | undefined>(undefined)

  useEffect(() => () => clearTimeout(timer.current), [])

  const debounced = useCallback(
    (inputValue: string) => {
      clearTimeout(timer.current)
      settleSuperseded.current?.([])
      settleSuperseded.current = undefined

      if (inputValue === '') return loadOptions?.(inputValue) ?? Promise.resolve([])

      return new Promise<SelectOption[]>((resolve, reject) => {
        settleSuperseded.current = resolve
        timer.current = setTimeout(() => {
          settleSuperseded.current = undefined
          loadOptions?.(inputValue).then(resolve, reject)
        }, SEARCH_DEBOUNCE_MS)
      })
    },
    [loadOptions]
  )

  return loadOptions ? debounced : undefined
}

// react-select annotates its built-in components as returning @emotion/react's
// JSX.Element, which isn't assignable to React 19's stricter JSX element type.
// Re-type them as plain React components — runtime behaviour is unchanged.
const RsMenu = selectComponents.Menu as unknown as React.ComponentType<any>
const RsClearIndicator = selectComponents.ClearIndicator as unknown as React.ComponentType<any>
const RsMultiValueLabel = selectComponents.MultiValueLabel as unknown as React.ComponentType<any>

// A chip crops a long label with an ellipsis, so the full text rides on a title
const MultiValueLabel = (props: any) => (
  <RsMultiValueLabel {...props} innerProps={{ ...props.innerProps, title: props.data.label }} />
)

type AsyncProps<Option = unknown, IsMulti extends boolean = true> = React.ComponentProps<
  typeof ReactAsyncSelect<Option, IsMulti>
>

type CommonAsyncSelectProps = {
  displayName: string
  hideLabel?: boolean
  defaultOptions?: SelectOption[] | string[] | true
  defaultValue?: SelectOption | SelectOption[]
  components?: AsyncProps['components']
  loadOptions?: (inputValue: string) => Promise<SelectOption[]>
  totalCount?: number
  noOptionsMessage?: string
  isSearchable?: boolean
  debounceSearch?: boolean
  isMulti?: boolean
  isCreatable?: boolean
  isClearable?: boolean
  createOptionPrefix?: string
  dataTestId?: string
  disabled?: boolean
  autoFocus?: boolean
  placeholder?: string
  className?: string
  errors?: any
  menuPortalTarget?: HTMLElement | null
  menuPosition?: 'absolute' | 'fixed'
  onChange?: (value: SelectOption | SelectOption[]) => void
  value?: SelectOption | SelectOption[]
  ref?: Ref<SelectInstance>
}

// With react-hook-form: pass `field` from useController
type WithFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = CommonAsyncSelectProps & {
  field: ControllerRenderProps<TFieldValues, TName>
  name?: string
}

// Standalone: pass `name`, `onChange`, and `value` directly
type StandaloneProps = CommonAsyncSelectProps & {
  field?: never
  name: string
}

type AsyncSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = WithFormFieldProps<TFieldValues, TName> | StandaloneProps

const AsyncSelect = function AsyncSelect({
  displayName,
  hideLabel,
  defaultOptions,
  defaultValue,
  loadOptions,
  components = {},
  field,
  name,
  totalCount,
  noOptionsMessage,
  isCreatable = true,
  isSearchable = true,
  debounceSearch = true,
  isMulti = true,
  isClearable = true,
  createOptionPrefix = tShared('buttons.create'),
  dataTestId,
  disabled = false,
  autoFocus = false,
  placeholder,
  className,
  errors,
  menuPortalTarget,
  menuPosition = 'absolute',
  onChange,
  value,
  ref,
}: AsyncSelectProps<any, any>) {
  const debouncedLoadOptions = useDebouncedLoadOptions(loadOptions)
  const searchOptions = debounceSearch ? debouncedLoadOptions : loadOptions
  const Menu = ({ children, ...props }: MenuProps<any>) => {
    // we have one Create new option which shouldn't be counted in the count
    const selectableOption = props.options.filter((option) => !option.label.includes('Create'))
    const optionsLength = selectableOption.length

    return (
      <RsMenu {...props}>
        <div className="tw:flex tw:flex-col tw:h-full ">
          {children}
          {!!totalCount && totalCount > 0 && optionsLength > 0 && (
            <div className="tw:w-full tw:py-2 tw:text-center tw:bg-primary-50 tw:text-primary-600">
              <p>
                {tShared('placeholders.dropdown_search_results', {
                  name: displayName.toLowerCase(),
                  optionsLength,
                  totalCount,
                })}
              </p>
              {totalCount > optionsLength && (
                <p className="tw:text-sm">{tShared('placeholders.dropdown_search_results_more_results')}</p>
              )}
            </div>
          )}
        </div>
      </RsMenu>
    )
  }

  if (isSearchable) {
    components.Menu = Menu
  }

  // Custom ClearIndicator with proper accessibility
  const ClearIndicator = (props: any) => {
    return (
      <RsClearIndicator
        {...props}
        innerProps={{
          ...props.innerProps,
          'aria-label': tShared('aria_labels.clear_object', { name: displayName }),
          role: 'button',
          'aria-hidden': 'false',
        }}
      />
    )
  }

  if (isClearable) {
    components.ClearIndicator = ClearIndicator
  }

  components.MultiValueLabel ??= MultiValueLabel

  const fieldName = field?.name ?? name ?? displayName
  const { fieldError: errorsById } = useFormField(fieldName, errors)

  const labelClassName = hideLabel ? 'tw:sr-only' : 'tw:mb-0 tw:pb-1 tw:text-sm tw:text-gray-700 tw:font-medium'

  // This is a workaround for a bug caused by an interaction between react select and radix UI dialog
  // This causes focus to not be able to leave the select component when it's in a dialog
  // See: https://github.com/JedWatson/react-select/issues/5732
  const onBlurWorkaround = (event: React.FocusEvent<HTMLInputElement>) => {
    const element = event.relatedTarget
    if (element && (element.tagName === 'A' || element.tagName === 'BUTTON' || element.tagName === 'INPUT')) {
      ;(element as HTMLElement).focus()
    }
  }

  const handleBlur = (e) => {
    field?.onBlur()
    onBlurWorkaround(e)
  }

  const customClassNames = {
    control: () =>
      clsx(
        'tw:border tw:rounded-lg tw:border-gray-400 tw:min-h-10.5 tw:focus-within:ring-4',
        !errorsById && 'tw:focus-within:border-primary-400 tw:ring-primary-100',
        errorsById && 'tw:border-red-400 tw:focus-within:border-red-500 tw:focus-within:ring-red-200'
      ),
    input: () => 'tw:m-0 tw:p-0',
    placeholder: () => 'tw:whitespace-nowrap',
    singleValue: () => 'tw:m-0',
    valueContainer: () => 'tw:px-3 tw:py-1',
    menuList: () => 'tw:max-h-full',
  }

  const multiValueFnc = () => {
    return field?.value?.map((value: string) => {
      return value
    })
  }

  const multiOnChange = (value: SelectOption[]) => {
    field?.onChange(value.map((option) => option))
  }

  const AsyncComponent = isCreatable ? AsyncCreatableSelect : ReactAsyncSelect

  const createProps: any = {}
  if (isCreatable) {
    createProps.formatCreateLabel = (value) => `${createOptionPrefix} "${value}"`
  }

  const onChangeHandler = (value: SelectOption | SelectOption[]) => {
    if (onChange) {
      onChange(value)
      return
    }

    if (isMulti) {
      multiOnChange(value as SelectOption[])
    } else {
      field?.onChange(value)
    }
  }

  const createStableKey = () => {
    const currentValue = value ?? field?.value
    const fieldIsEmpty = !currentValue || (Array.isArray(currentValue) && currentValue.length === 0)

    return fieldIsEmpty ? `${fieldName}-empty` : `${fieldName}-has-value`
  }

  return (
    <>
      <label htmlFor={fieldName} className={labelClassName} id={`${fieldName}.label`} data-testid={dataTestId}>
        {displayName}
      </label>
      <AsyncComponent
        key={createStableKey()}
        isDisabled={disabled}
        autoFocus={autoFocus}
        components={components}
        className={className ?? 'tw:w-full'}
        isSearchable={isSearchable}
        isMulti={isMulti}
        isClearable={isClearable}
        noOptionsMessage={() =>
          noOptionsMessage ?? tShared('placeholders.placeholder_search', { name: displayName.toLowerCase() })
        }
        onBlur={handleBlur}
        loadingMessage={() => `${tShared('labels.searching')}...`}
        placeholder={placeholder ?? tShared('placeholders.placeholder_add', { name: displayName.toLowerCase() })}
        defaultOptions={defaultOptions}
        defaultValue={defaultValue}
        loadOptions={searchOptions}
        onChange={onChangeHandler}
        classNames={customClassNames}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPosition}
        // A portaled menu lands under a Radix modal's pointer-events:none body, so re-enable them
        styles={{
          menu: (base) => ({ ...base, zIndex: 50 }),
          // A chip wider than the field would sit alone on its line and push the empty
          // text input onto a blank second line; cap it so the input fits beside it
          multiValue: (base) => ({ ...base, maxWidth: 'calc(100% - 1rem)', margin: '1px 2px' }),
          ...(menuPortalTarget ? { menuPortal: (base) => ({ ...base, zIndex: 9999, pointerEvents: 'auto' }) } : {}),
        }}
        aria-labelledby={`${fieldName}.label`}
        aria-invalid={errorsById ? 'true' : 'false'}
        aria-errormessage={errorsById ? `${fieldName}.error` : undefined}
        value={value ?? (isMulti ? multiValueFnc() : field?.value)}
        ref={ref}
        {...createProps}
      />
      <div role="status" aria-relevant="all" data-testid={`${fieldName}.error`}>
        {errorsById && (
          <p id={`${fieldName}.error`} className="tw:text-red-600 tw:font-light tw:text-sm tw:pt-1">
            {errorsById.message}
          </p>
        )}
      </div>
    </>
  )
}

export default AsyncSelect
